import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type {
  ClientDetail,
  CreateClientPayload,
  PaginatedClients,
  UpdateClientPayload,
} from './clients.types';

/**
 * Hooks de React Query para /api/v1/clients. El access token JWT viaja
 * automáticamente por el interceptor de axios (Authorization: Bearer).
 */

export function useClients(params: { page?: number; pageSize?: number; search?: string }) {
  return useQuery({
    queryKey: ['clients', 'list', params],
    queryFn: async () => {
      const r = await api.get<PaginatedClients>('/clients', { params });
      return r.data;
    },
    staleTime: 15_000,
  });
}

export function useClient(id: string | null | undefined) {
  return useQuery({
    // null/undefined id → query deshabilitada (la vista detalle puede
    // esperar al navigate-id param)
    enabled: Boolean(id),
    queryKey: ['clients', 'detail', id],
    queryFn: async () => {
      const r = await api.get<ClientDetail>(`/clients/${id}`);
      return r.data;
    },
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateClientPayload) => {
      const r = await api.post<ClientDetail>('/clients', payload);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clients', 'list'] });
    },
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateClientPayload }) => {
      const r = await api.put<ClientDetail>(`/clients/${id}`, patch);
      return r.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['clients', 'list'] });
      void qc.invalidateQueries({ queryKey: ['clients', 'detail', data.id] });
    },
  });
}